// PHASE_5 / B3-frontend — per-feature i18n keys for the spec-42 admin console
// (Ayarlar — settings + company profile + branding).
//
// Mirrors the notifications/assets/benefits convention: this file is the
// per-feature SSOT export of the en-US + tr-TR key objects (under the
// `admin_settings.*` namespace). The SAME keys are merged into the runtime
// locale bundles (src/i18n/locales/{en-US,tr-TR}.json) so react-i18next resolves
// them. Every user-facing string is a t("admin_settings.*") key (ADR-006 — no
// literals; lint REJECTS hardcoded literals). NOTHING here is money.
//
// Turkish is primary for these İSTON screens (ADR-014 §6): Kurum Bilgileri /
// Marka / Şirket Adı. The tab labels map the iston settings tab bar onto the
// spec-42 surfaces.

export const ADMIN_SETTINGS_I18N_EN = {
  admin_settings: {
    title: "Settings",
    description: "Company profile, branding, and tenant configuration.",
    lead: "Company profile, branding, and tenant configuration — one control point.",
    loading: "Loading settings…",
    error_title: "Couldn't load settings",
    save: "Save",
    saving: "Saving…",
    saved: "Saved",
    // Tabs (map the iston tab bar onto spec-42 surfaces).
    tab_company: "Company Profile",
    tab_branding: "Branding",
    tab_users: "Users",
    tab_sso: "SSO & Security",
    tab_audit_log: "Audit Log",
    tabs_label: "Settings sections",
    // Company profile
    company_title: "Company Profile",
    company_description: "Display name, locale, time zone, and fiscal settings.",
    company_empty: "No company settings yet.",
    company_official_note:
      "These records are the official source for payroll, social-security, and e-government filings.",
    company_display_name: "Company name",
    company_website: "Website",
    company_phone: "Phone",
    company_default_locale: "System language",
    company_default_time_zone: "Time zone",
    company_fiscal_year_start: "Fiscal year start",
    residency_region: "Data residency region",
    read_only: "Read-only",
    residency_read_only_note:
      "Data residency is fixed when the account is created and cannot be changed here.",
    field_value: "Value",
    field_category: "Category",
    // Branding
    branding_title: "Branding",
    branding_description: "Brand colors and email header applied on top of the baseline.",
    branding_color_primary: "Primary color",
    branding_color_secondary: "Secondary color",
    branding_color_accent: "Accent color",
    branding_email_header: "Email header style",
    branding_color_hint: "Hex like #2563EB.",
    email_header: {
      minimal: "Minimal",
      centered_logo: "Centered logo",
      brand_band: "Brand band",
    },
    error_hex: "Use a #RRGGBB hex color.",
    none: "—",
    // Users and permission assignments
    users_loading: "Loading users and assignments…",
    users_error_title: "Couldn't load permission assignments",
    users_grant_title: "Grant a permission profile",
    users_grant_description: "Assign one existing permission profile to a specific employee.",
    users_employee_label: "Employee",
    users_employee_placeholder: "Select an employee",
    users_profile_label: "Permission profile",
    users_profile_placeholder: "Select a profile",
    users_reason_label: "Reason (optional)",
    users_company_scope_label: "Company access",
    users_company_scope_holding: "All companies in the group",
    users_company_scope_selected: "Only the companies I choose",
    users_company_scope_companies_label: "Companies this access covers",
    users_company_scope_empty: "Choose at least one company.",
    users_grant_action: "Grant profile",
    users_granting: "Granting…",
    users_resolved_title: "Resolved assignments",
    users_resolved_description: "Assignments whose holders can be listed from tenant data.",
    users_all_employees: "Applies to everyone",
    users_no_resolved_holders: "No holders were returned for this resolved assignment.",
    users_more_holders: "and {{count}} more",
    users_unresolvable_title: "Rule-based assignments",
    users_unresolvable_description:
      "These assignments apply through targets whose membership this screen cannot enumerate.",
    users_target_kind: "Applies to: {{kind}}",
    users_role: "Role: {{role}}",
    users_revoke: "Revoke",
    users_revoking: "Revoking…",
    users_empty_title: "No active assignments",
    users_empty_description: "Grant a permission profile to an employee to create an assignment.",
    users_load_more: "Load more assignments",
    // Tenant audit log
    audit_title: "Audit Log",
    audit_description: "Tenant activity with actors, affected entities, and changed field names.",
    audit_filter_action_label: "Action",
    audit_loading: "Loading audit events…",
    audit_error_title: "Couldn't load the audit log",
    audit_empty_title: "No audit events",
    audit_empty_description: "No tenant audit events match the current action filter.",
    audit_actor_label: "Actor",
    audit_actor_unresolved: "Name unresolved",
    audit_actor_redacted: "Personal data removed",
    audit_entity_label: "Entity",
    audit_changed_fields_label: "Changed fields",
    audit_changed_fields_none: "No field-level changes",
    audit_forbidden_title: "You don't have access",
    audit_forbidden_description:
      "{{baseline}} Audit Log also requires the perm_audit_read grant, which your account does not currently hold. Ask a tenant admin to grant access.",
    audit_filter_all: "All activity",
    audit_code_unknown: "System code",
    audit_entity_id_title: "Record id",
    audit_target_deleted: "Record deleted",
    audit_filter_entity_label: "Entity type",
    audit_filter_all_entities: "All entity types",
    audit_filter_from_label: "From",
    audit_filter_to_label: "To",
    audit_page_size_label: "Rows",
    audit_search_label: "Search",
    audit_search_placeholder: "Action, person, record\u2026",
    audit_search_hint: "Filters the rows on this page. Use the filters above to narrow the whole log.",
    audit_table_caption: "Tenant audit events",
    audit_column_time: "Time",
    audit_column_action: "Action",
    audit_column_actor: "Actor",
    audit_column_entity: "Entity",
    audit_column_changed_fields: "Changed fields",
    audit_page_indicator: "Page {{page}} \u00b7 {{shown}} of {{loaded}} rows shown",
    audit_prev_page: "Previous",
    audit_next_page: "Next",
    audit_no_match_title: "Nothing on this page matches",
    audit_no_match_description:
      "No row on page matches \u201c{{term}}\u201d. Clear the box, or narrow the whole log with the filters above.",
    audit_export_csv: "Export CSV",
    audit_exporting: "Preparing\u2026",
    audit_csv_filename: "audit-log.csv",
    audit_csv_action_code: "Action code",
    audit_export_truncated_title: "Export stopped at the row limit",
    audit_export_truncated_description:
      "The file holds the {{count}} most recent matching events \u2014 the export ceiling is {{cap}} rows and this filter returned more. Narrow the date range to export the rest.",
    audit_export_error_title: "Export failed",
    // Vocabulary — the shared translation catalogue for values that arrive from
    // the API as schema identifiers. These screens used to print them raw:
    // `region_tr`, `role_super_admin`, `entity_permission_assignment`,
    // `session.me.fetched` (SEM-5585). audit_action / audit_entity double as the
    // SSOT for which codes are KNOWN — vocabulary.ts derives that set from
    // this tree, so a list and its labels cannot drift apart.
    audit_action: {
      ai: {
        call: {
          completed: "AI call completed",
          refused_gate: "AI request refused by a guardrail",
        },
      },
      approval_chain_template: {
        created: "Approval chain template created",
        deactivated: "Approval chain template deactivated",
        updated: "Approval chain template updated",
      },
      approval_request: { approve: "Approval request approved" },
      asset: {
        created: "Asset created",
        write_off_requested: "Asset write-off requested",
        written_off: "Asset written off",
      },
      assignment: {
        force_ended: "Asset assignment force-ended",
        initiated: "Asset assigned",
        returned: "Asset returned",
      },
      audit: { log_read: "Audit log read" },
      availability_window: {
        created: "Availability window added",
        deleted: "Availability window removed",
      },
      benefit: { enrolled: "Benefit enrolment recorded" },
      benefit_plan: {
        archived: "Benefit plan archived",
        created: "Benefit plan created",
        published: "Benefit plan published",
      },
      branding_config: { updated: "Branding updated" },
      calendar_event: {
        booked: "Calendar event booked",
        cancelled: "Calendar event cancelled",
        created: "Calendar event created",
        rescheduled: "Calendar event moved",
      },
      carrier: { connected: "Benefit carrier connected" },
      device: { registered: "Device registered", updated: "Device updated" },
      device_credential: {
        issued: "Device credential issued",
        revoked: "Device credential revoked",
        rotated: "Device credential rotated",
      },
      employee: {
        preferred_locale: { updated: "Employee language preference updated" },
        ui_tour_seen_version: { updated: "Product tour marked as seen" },
      },
      garnishment_deduction: { recorded: "Garnishment deduction recorded" },
      garnishment_order: {
        created: "Garnishment order created",
        satisfied_in_full: "Garnishment order paid in full",
      },
      handover_record: { signed: "Handover record signed" },
      integrations: {
        scim: { user_provisioning_accepted: "SCIM user provisioning accepted" },
      },
      integration: {
        installed: "Integration connected",
        paused: "Integration paused",
        reconfigured: "Integration reconfigured",
        resumed: "Integration resumed",
        uninstalled: "Integration disconnected",
      },
      kiosk: { pin_locked: "Kiosk PIN locked out" },
      kiosk_pin_credential: {
        issued: "Kiosk PIN issued",
        revoked: "Kiosk PIN revoked",
      },
      leave_request: {
        approved: "Leave request approved",
        cancelled: "Leave request cancelled",
        created: "Leave request drafted",
        rejected: "Leave request rejected",
        submitted: "Leave request submitted",
      },
      payroll_tr: {
        master_data_updated: "Payroll master data updated",
        workplace_created: "Workplace (SGK) created",
        workplace_updated: "Workplace (SGK) updated",
      },
      pulse_cycle: {
        paused: "Pulse survey cycle paused",
        started: "Pulse survey cycle started",
      },
      schedule: { published: "Shift schedule published" },
      scim_bearer_token: {
        issued: "SCIM token issued",
        revoked: "SCIM token revoked",
      },
      notification: {
        marked_read: "Notification marked read",
        preference_updated: "Notification preference updated",
        sent: "Notification sent",
      },
      onboarding_instance: { started: "Onboarding started" },
      onboarding_plan: {
        created: "Onboarding plan created",
        published: "Onboarding plan published",
      },
      onboarding_task: { transitioned: "Onboarding task moved on" },
      org_scenario_apply: {
        department_change: "Department change applied",
        dissolved_department: "Department dissolved",
        manager_change: "Manager change applied",
        new_department: "Department created",
        promotion: "Promotion applied",
        reporting_relationship_add: "Reporting relationship added",
        reporting_relationship_remove: "Reporting relationship removed",
        team_add: "Added to a team",
        team_remove: "Removed from a team",
        team_role_change: "Team role changed",
        termination: "Termination applied",
      },
      orphaned_punch: { purged: "Orphaned clock punch purged" },
      notification_default: {
        cleared: "Notification default cleared",
        updated: "Notification default changed",
      },
      pay_run: {
        approval_requested: "Pay run sent for approval",
        approved: "Pay run approved",
        calculated: "Pay run calculated",
        created: "Pay run created",
        finalize_enqueued: "Pay run queued for finalisation",
        finalized: "Pay run finalized",
        previewed: "Pay run previewed",
      },
      pay_statement: { created: "Payslip created" },
      payslip_artifact: { issued: "Payslip document issued" },
      permission_assignment: {
        granted: "Permission profile granted",
        revoked: "Permission profile revoked",
      },
      permission_profile: {
        created: "Permission profile created",
        updated: "Permission profile updated",
      },
      platform: { healthz: { checked: "Service health check" } },
      presence_session: { closed: "Presence session closed" },
      pto_balance: {
        drawn: "Leave balance drawn down",
        restored: "Leave balance restored",
      },
      recognition: { given: "Recognition given" },
      sar_export: { bundle_assembled: "Subject access bundle assembled" },
      // Historical rows only — the action was retired in SEM-5583 but ten-year
      // retention keeps the existing ones readable.
      session: { me: { fetched: "Session details read" } },
      severance_calculation: { computed: "Severance calculated" },
      shift: { swap_requested: "Shift swap requested" },
      support_inquiry: { created: "Support enquiry created" },
      support_ticket: {
        created: "Support ticket created",
        escalated: "Support ticket escalated",
      },
      survey: { launched: "Survey launched" },
      survey_response: { submitted: "Survey response submitted" },
      task: { completed: "Task completed" },
      admin: { setup_completed: "Workspace setup completed" },
      tenant_setting: { changed: "Setting changed" },
      time_entry: {
        created: "Time entry created",
        locked: "Time entry locked",
        updated: "Time entry updated",
      },
      time_payroll_export: {
        consumed: "Payroll export consumed by a pay run",
        ready: "Payroll export ready",
      },
      timesheet: {
        approved: "Timesheet approved",
        assembled: "Timesheet assembled",
        locked: "Timesheet locked",
        reopened: "Timesheet reopened",
        submitted: "Timesheet submitted",
      },
    },
    audit_entity: {
      ai_call: "AI call",
      entity_approval_chain_template: "Approval chain template",
      entity_approval_request: "Approval request",
      entity_asset: "Asset",
      entity_asset_assignment: "Asset assignment",
      entity_asset_audit_cycle: "Asset audit cycle",
      entity_asset_handover_record: "Asset handover record",
      entity_audit_event: "Audit event",
      entity_availability_window: "Availability window",
      entity_beneficiary: "Beneficiary",
      entity_benefit_carrier: "Benefit carrier",
      entity_benefit_plan: "Benefit plan",
      entity_branding_config: "Branding settings",
      entity_calendar_event: "Calendar event",
      entity_department: "Department",
      entity_dependent: "Dependent",
      entity_device: "Device",
      entity_device_credential: "Device credential",
      entity_employee: "Employee",
      entity_enrollment: "Benefit enrolment",
      entity_garnishment_deduction: "Garnishment deduction",
      entity_garnishment_order: "Garnishment order",
      entity_integration: "Integration",
      entity_kiosk_pin_credential: "Kiosk PIN credential",
      entity_location: "Location",
      entity_notification: "Notification",
      entity_notification_delivery: "Notification delivery",
      entity_notification_preference: "Notification preference",
      entity_notification_tenant_default: "Notification default",
      entity_ocr_job: "OCR job",
      entity_onboarding_instance: "Onboarding run",
      entity_onboarding_plan: "Onboarding plan",
      entity_onboarding_task: "Onboarding task",
      entity_org_scenario: "Org scenario",
      entity_orphaned_punch: "Orphaned clock punch",
      entity_pay_run: "Pay run",
      entity_pay_statement_tr: "Payslip",
      entity_payslip_artifact: "Payslip document",
      entity_permission_assignment: "Permission assignment",
      entity_permission_profile: "Permission profile",
      entity_presence_session: "Presence session",
      entity_pto_balance: "Leave balance",
      entity_pto_request: "Leave request",
      entity_pulse_cycle: "Pulse survey cycle",
      entity_qle_event: "Qualifying life event",
      entity_recognition: "Recognition",
      entity_report_run: "Report run",
      entity_reporting_relationship: "Reporting relationship",
      entity_sar_request: "Subject access request",
      entity_schedule: "Shift schedule",
      entity_scheduled_deduction: "Scheduled deduction",
      entity_scheduled_export: "Scheduled export",
      entity_severance_calculation: "Severance calculation",
      entity_shift: "Shift",
      entity_signature_request: "Signature request",
      entity_support_inquiry: "Support enquiry",
      entity_support_ticket: "Support ticket",
      entity_survey: "Survey",
      entity_survey_response: "Survey response",
      entity_task: "Task",
      entity_team_membership: "Team membership",
      entity_tenant: "Company",
      entity_tenant_setting: "Company setting",
      entity_time_entry: "Time entry",
      entity_time_payroll_export_batch: "Payroll export batch",
      entity_timesheet: "Timesheet",
      scim_request: "SCIM request",
      session: "Session",
      system: "System",
    },
    target_kind: {
      everyone: "Everyone",
      role: "A role",
      explicit_ids: "Named employees",
      attribute_rule: "An attribute rule",
      supergroup: "A supergroup",
    },
    role_name: {
      role_super_admin: "Super admin",
      role_admin: "Administrator",
      role_executive: "Executive",
      role_hr: "HR",
      role_team_lead: "Team lead",
      role_sar_auditor: "Data-request auditor",
      role_hr_admin: "HR admin",
      role_it_admin: "IT admin",
      role_manager: "Manager",
      role_finance: "Finance",
      role_compliance_officer: "Compliance officer",
      role_support_agent: "Support agent",
      role_employee: "Employee",
    },
    // C8 — SSO & Security. Every value here is a fact read live from the
    // identity provider or counted from this tenant's own directory; nothing on
    // this tab is editable, because realm posture is deployment-wide.
    sso_title: "How people sign in",
    sso_description: "Read live from the identity provider that authenticates your people.",
    sso_deployment_note:
      "These are deployment-wide settings, shared by every organisation on this installation — they are shown here so you know what applies to you, not so one organisation can change them.",
    sso_loading: "Loading sign-in settings…",
    sso_error_title: "Couldn't load sign-in settings",
    sso_not_configured_title: "No identity provider connected",
    sso_not_configured_body:
      "This installation has no identity-provider credentials configured, so sign-in settings cannot be read. Access coverage below still reflects your own records.",
    sso_unreachable_title: "Identity provider did not answer",
    sso_unreachable_body:
      "The settings below could not be read just now. This is not the same as \"nothing is enforced\" — the previous configuration is still in force.",
    sso_realm: "Identity realm",
    sso_checked_at: "Checked {{time}}",
    sso_first_factor: "Sign-in credential",
    sso_otp: "One-time code",
    sso_password_policy: "Password rules",
    sso_password_policy_on: "Enforced",
    sso_password_policy_off: "None",
    sso_self_registration: "Self sign-up",
    sso_password_reset: "Self-service password reset",
    sso_login_with_email: "Sign in with email address",
    sso_remember_me: "Stay signed in",
    sso_brute_force: "Lockout after failed attempts",
    sso_brute_force_on: "After {{count}} attempts",
    sso_session_idle: "Idle timeout",
    sso_session_max: "Maximum session length",
    sso_enabled: "On",
    sso_disabled: "Off",
    sso_no_required_credential: "No credential declared",
    // Identity providers
    sso_providers_title: "Identity providers",
    sso_providers_description: "External directories your people can sign in through.",
    sso_providers_empty_title: "No external provider connected",
    sso_providers_empty_body:
      "Your people sign in with NovemAI's own identity server. Connecting a corporate directory is a separate setup step.",
    sso_provider_disabled: "Disabled",
    // Directory coverage
    sso_coverage_title: "Who can sign in",
    sso_coverage_description: "Employees with a sign-in identity linked to their record.",
    sso_coverage_linked: "{{linked}} of {{total}} employees",
    sso_coverage_all_linked: "Everyone currently employed can sign in.",
    sso_coverage_unlinked: "{{count}} without a sign-in identity",
    sso_coverage_unlinked_note:
      "They have an employee record but no way to sign in yet. People who have left the company are not counted.",
    // Caller's own sessions
    sso_sessions_title: "Your active sessions",
    sso_sessions_description: "Sessions currently open under your own account.",
    sso_sessions_empty: "No other active session.",
    sso_session_started: "Started {{time}}",
    sso_session_last_seen: "Last active {{time}}",
    sso_session_ip: "IP {{ip}}",
    // Integrations (C9). Connector titles are keyed by connector_key; an
    // unknown key prints as itself rather than as an invented name.
    tab_integrations: "Integrations",
    tab_notifications: "Notification Settings",
    tab_billing: "Billing",
    // Companies (SEM-6040). DISTINCT from tab_company: that tab holds the
    // holding's own preferences; this one is the registry of legal entities
    // the holding operates.
    tab_companies: "Companies",
    companies_title: "Companies",
    companies_description:
      "The legal entities this organisation operates. Every employee belongs to one, and payroll prints its tax number.",
    companies_loading: "Loading companies…",
    companies_error_title: "Couldn't load companies",
    companies_empty: "No companies yet.",
    companies_unverified: "Not confirmed",
    companies_unverified_hint:
      "This record was created by the system and nobody has confirmed it yet.",
    companies_default: "Default",
    companies_archived: "Archived",
    companies_show_archived: "Show archived",
    companies_name: "Company name",
    companies_country: "Country (ISO 3166-1 alpha-2)",
    companies_currency: "Currency (ISO 4217)",
    companies_vkn: "Tax number (VKN)",
    companies_vkn_line: "Tax number: {{value}}",
    companies_value_none: "—",
    companies_mersis: "MERSIS number",
    companies_registration: "Trade registry number",
    companies_add_title: "Add a company",
    companies_add_submit: "Add",
    companies_adding: "Adding…",
    companies_edit: "Edit",
    companies_cancel: "Cancel",
    companies_save: "Save",
    companies_saving: "Saving…",
    companies_archive: "Archive",
    companies_archiving: "Archiving…",
    companies_vkn_locked:
      "Payslips have already been issued for this company, so its tax number can no longer be changed.",
    notif_title: "Notification channels",
    notif_description:
      "Set the default for each event. Employees can narrow it further from their own profile; their choice always wins.",
    notif_undeliverable:
      "Only in-app and e-mail are delivered by this build. {{channels}} are not sent, whatever is configured.",
    notif_email_unconfigured:
      "No e-mail relay is configured on this deployment, so e-mail is not delivered even where it is switched on.",
    notif_col_event: "Event",
    notif_col_notify: "Notify",
    notif_col_notify_help:
      "Controls whether the event lands in the in-app inbox; switching it off also stops that event's e-mail.",
    notif_col_email: "E-mail",
    notif_col_cadence: "E-mail cadence",
    notif_mandatory: "Mandatory",
    notif_email_only:
      "Always delivered to everyone's inbox — only its e-mail can be switched off.",
    notif_no_email: "This event does not send e-mail.",
    notif_cannot_disable:
      "Cannot be switched off: it only reaches the inbox, and the inbox record is written whatever this is set to.",
    notif_overridden: "Company setting",
    notif_reset: "Back to default",
    notif_notify_aria: "Notify for: {{event}}",
    notif_email_aria: "Send e-mail for: {{event}}",
    notif_cadence_aria: "How often {{event}} e-mails arrive",
    notification_group: {
      announcement: "Announcements",
      asset: "Assets",
      approval_request: "Approvals",
      calendar_event: "Calendar",
      employee: "People",
      kiosk: "Kiosk & devices",
      leave: "Leave",
      schedule: "Shift schedule",
      shift: "Shifts",
      support_ticket: "Support",
      support_ticket_event: "Support replies",
      policy_publication: "Policies",
      onboarding_buddy: "Onboarding buddy",
      review_response: "Review forms",
      review: "360 reviews",
      audit_cycle: "Asset audits",
      payslip: "Payslips",
      learning: "Training",
    },
    notification_channel: {
      email: "E-mail",
      in_app: "In-app",
      sms: "SMS",
      push: "Push notifications",
      whatsapp: "WhatsApp",
      slack: "Slack",
      teams: "Microsoft Teams",
      webhook: "Webhooks",
    },
    notification_type: {
      audit_cycle: {"started": "Asset audit started"},
      payslip: { available: "Payslip available" },
      onboarding_buddy: {"proposed": "Buddy invitation", "accepted": "Buddy pairing accepted", "declined": "Buddy pairing declined", "attention_required": "Buddy program needs action"},
      policy_publication: { available: "New policy to read", reminder: "Policy reading reminder" },
      review_response: { assigned: "Review form assigned to you" },
      review: { shared: "Your 360 review results were shared" },
      learning: { reminder: "Training reminder" },

      announcement: { published: "New company announcement", reminder: "Announcement acknowledgement reminder" },
      asset: {
        assigned: "An asset was assigned to you",
        vehicle: {
          trafik_sigortasi_expiring: "Vehicle traffic insurance expiring",
          muayene_expiring: "Vehicle inspection expiring",
          muayene_expired: "Vehicle inspection expired",
        },
      },
      approval_request: {
        approved: "Your request was approved",
        cancelled: "Your request was cancelled",
        created: "Your approval is needed",
        expired: "Your request expired",
        rejected: "Your request was rejected",
        step_advanced: "Approval moved to your step",
      },
      calendar_event: {
        cancelled: "Meeting cancelled",
        created: "New meeting invitation",
        reminder_due: "Meeting is starting soon",
        rescheduled: "Meeting moved",
      },
      employee: {
        hired: "New colleague joined",
      },
      kiosk: {
        pin_locked: "Kiosk PIN locked out",
      },
      leave: {
        approved: "Leave approved",
        cancelled: "Leave cancelled",
        rejected: "Leave rejected",
      },
      schedule: {
        published: "Shift schedule published",
      },
      shift: {
        swapped: "Shift swapped",
      },
      support_ticket: {
        assigned: "Support ticket assigned to you",
        created: "Support ticket opened",
        resolved: "Support ticket resolved",
        sla_breached: "Support ticket missed its SLA",
      },
      support_ticket_event: {
        appended: "Reply on a support ticket",
      },
    },
    connectors_loading: "Loading integrations…",
    connectors_error_title: "Couldn't load integrations",
    connectors_thin_note:
      "Deliveries carry what happened and when — never employee data. Your endpoint receives the event type and a record id, not names, salaries or identifiers.",
    connectors_insecure_note:
      "This installation allows plain http:// and internal addresses. That is a development setting and must not be on in production.",
    connector_webhook_title: "Webhook — outbound event stream",
    connector_webhook_blurb:
      "Send leave, payroll and personnel events to your own system the moment they happen.",
    connector_slack_title: "Slack",
    connector_slack_blurb: "Recognition and announcements in your workspace.",
    connector_state_connected: "Connected",
    connector_state_paused: "Paused",
    connector_state_errored: "Failing",
    connector_state_available: "Available",
    connector_state_unconfigured: "Not set up",
    connector_unconfigured_body:
      "This connector needs values your Novem HR installation has not been given yet. Ask whoever runs the installation to set:",
    connector_destination: "Destination address",
    connector_destination_placeholder: "https://hooks.example.com/novemai",
    connector_connect: "Connect",
    connector_save_destination: "Save address",
    connector_test: "Test connection",
    connector_sync_now: "Send now",
    connector_pause: "Pause",
    connector_resume: "Resume",
    connector_remove: "Disconnect",
    connector_last_sync: "Last delivery",
    connector_never_synced: "Nothing delivered yet",
    connector_pending: "Waiting to be sent",
    connector_last_error: "Last failure: {{code}}",
    connector_probe_ok: "Your endpoint answered {{status}} in {{ms}} ms.",
    connector_probe_failed: "Your endpoint did not accept the call ({{reason}}).",
    connector_sync_ok: "{{count}} event(s) delivered.",
    connector_sync_nothing: "Nothing new to send.",
    connector_sync_failed: "Delivery failed ({{reason}}).",
    connector_run_ok: "{{count}} delivered",
    connector_run_failed: "Failed",
    connector_secret_title: "Save this signing secret now",
    connector_secret_body:
      "Your endpoint needs it to verify that a delivery really came from us. It is shown once and cannot be retrieved again — if it is lost, disconnect and reconnect to get a new one.",
    connector_secret_dismiss: "I have saved it",
    // SCIM provisioning tokens (spec 10).
    scim_tokens_title: "SCIM provisioning tokens",
    scim_tokens_blurb:
      "Let your identity provider create and update employee records automatically.",
    scim_tokens_loading: "Loading tokens…",
    scim_tokens_empty: "No token issued yet.",
    scim_token_label: "Token name",
    scim_token_label_placeholder: "Okta — production",
    scim_token_issue: "Create token",
    scim_token_revoke: "Revoke",
    scim_token_active: "Active",
    scim_token_revoked: "Revoked",
    scim_token_stale: "Unusable — create a new one",
    scim_token_unverified: "Cannot be verified",
    scim_token_copy: "Copy",
    scim_token_copied: "Copied",
    scim_token_new_title: "Copy this token now",
    scim_token_new_body:
      "Paste it into your identity provider's SCIM settings. It is shown once and cannot be retrieved again — if it is lost, revoke it and create a new one.",
    scim_token_dismiss: "I have saved it",
    scim_token_allowlist_note:
      "This installation also keeps a hash allowlist. Give this value to your system administrator, otherwise the token will be refused.",
    // Credential names as the identity provider reports them.
    sso_credential: {
      password: "Password",
      otp: "One-time code",
      "webauthn-passwordless": "Passkey",
      webauthn: "Security key",
      "kerberos": "Kerberos",
    },
    region: {
      region_tr: "Türkiye",
      region_eu: "European Union",
      region_us: "United States",
      region_apac_reserved: "Asia-Pacific (reserved)",
      region_ca_reserved: "Canada (reserved)",
      on_prem: "Your own servers",
    },
    unresolvable_reason: {
      target_kind_not_enumerable:
        "Membership for this target is evaluated when access is checked, so the members cannot be listed on this screen.",
    },
    // Sekme başlık satırları (SettingsTabHeader).
    users_description: "Who holds which permission profile, and where it applies.",
    sso_tab_description: "Sign-in method, identity providers, and your active sessions.",
    integrations_description: "Connectors, SCIM provisioning, and webhook endpoints.",
    audit_data_export_link: "Data export (KVKK)",
    // Company profile — identity card read from the default legal entity.
    company_hero_loading: "Loading company identity…",
    company_hero_error: "Couldn't load the company identity.",
    company_hero_source: "Read from the default company record.",
    company_hero_no_default: "No default company was found for your account.",
    company_hero_edit: "Edit",
    company_hero_vkn: "Tax ID (VKN)",
    company_hero_mersis: "MERSIS no",
    company_hero_address: "Address",
    company_hero_not_set: "Not set",
    // Integrations — sync all.
    connectors_sync_all: "Sync all",
    connectors_sync_all_results: "Sync results",
    // Webhook endpoints (/v1/webhooks).
    webhooks_title: "Webhook endpoints",
    webhooks_blurb: "Receive signed event deliveries at your own HTTPS address.",
    webhooks_loading: "Loading webhook endpoints…",
    webhooks_empty: "No webhook endpoints yet.",
    webhooks_no_delete_note:
      "Endpoints cannot be deleted. Pause an endpoint to stop deliveries to it.",
    webhook_url_label: "Endpoint URL",
    webhook_url_placeholder: "https://example.com/webhooks/novemai",
    webhook_events_label: "Events",
    webhook_events_hint:
      "Separate event names with commas. The server rejects names it does not know.",
    webhook_register: "Add endpoint",
    webhook_active: "Active",
    webhook_paused: "Paused",
    webhook_created_at: "Added {{time}}",
    webhook_pause: "Pause",
    webhook_resume: "Resume",
    webhook_rotate: "Rotate secret",
    webhook_rotate_confirm_body:
      "The current signing secret stops working immediately. Your receiver must use the new secret to verify deliveries.",
    webhook_rotate_confirm: "Rotate now",
    webhook_cancel: "Cancel",
    webhook_secret_title: "Save this signing secret now",
    webhook_secret_body:
      "This is the only time the secret is shown. If you lose it, rotate the secret to get a new one.",
    webhook_secret_dismiss: "I saved it",
    webhook_deliveries_title: "Recent deliveries",
    webhook_deliveries_show: "Show deliveries",
    webhook_deliveries_hide: "Hide deliveries",
    webhook_deliveries_loading: "Loading deliveries…",
    webhook_deliveries_empty: "No deliveries yet.",
    webhook_delivery_attempt: "Attempt {{n}}",
    webhook_delivery_http: "HTTP {{status}}",
    webhook_delivery_retry: "Retry",
    webhook_delivery_retry_queued: "Retry queued. The next delivery run sends it.",
    webhook_delivery_status: {
      pending: "Pending",
      delivered: "Delivered",
      failed: "Failed",
      dead_letter: "Stopped",
    },
  },
} as const;

export const ADMIN_SETTINGS_I18N_TR = {
  admin_settings: {
    title: "Ayarlar",
    description: "Şirket profili, markalama ve kiracı (tenant) yapılandırması.",
    lead: "Şirket profili, markalama ve kiracı yapılandırması — tek bir kontrol noktası.",
    loading: "Ayarlar yükleniyor…",
    error_title: "Ayarlar yüklenemedi",
    save: "Kaydet",
    saving: "Kaydediliyor…",
    saved: "Kaydedildi",
    tab_company: "Şirket Profili",
    tab_branding: "Markalama",
    tab_users: "Kullanıcılar",
    tab_sso: "SSO & Güvenlik",
    tab_audit_log: "Denetim Günlüğü",
    tabs_label: "Ayar bölümleri",
    company_title: "Şirket Profili",
    company_description: "Görünen ad, sistem dili, saat dilimi ve mali ayarlar.",
    company_empty: "Henüz şirket ayarı bulunmuyor.",
    company_official_note:
      "Bu kayıtlar bordro, sosyal güvenlik ve e-devlet bildirimleri için resmi kaynak niteliğindedir.",
    company_display_name: "Şirket adı",
    company_website: "Web sitesi",
    company_phone: "Telefon",
    company_default_locale: "Sistem dili",
    company_default_time_zone: "Saat dilimi",
    company_fiscal_year_start: "Mali yıl başlangıcı",
    residency_region: "Veri barındırma bölgesi",
    read_only: "Salt okunur",
    residency_read_only_note:
      "Veri barındırma bölgesi hesap oluşturulurken belirlenir ve buradan değiştirilemez.",
    field_value: "Değer",
    field_category: "Kategori",
    branding_title: "Markalama",
    branding_description: "Temel tasarıma uygulanan marka renkleri ve e-posta başlığı.",
    branding_color_primary: "Birincil renk",
    branding_color_secondary: "İkincil renk",
    branding_color_accent: "Vurgu rengi",
    branding_email_header: "E-posta başlığı stili",
    branding_color_hint: "Hex formatında (Örn: #2563EB).",
    email_header: {
      minimal: "Minimal",
      centered_logo: "Ortalanmış logo",
      brand_band: "Marka şeridi",
    },
    error_hex: "Lütfen #RRGGBB formatında bir hex rengi kullanın.",
    none: "—",
    users_loading: "Kullanıcılar ve atamalar yükleniyor…",
    users_error_title: "İzin atamaları yüklenemedi",
    users_grant_title: "İzin profili ata",
    users_grant_description: "Mevcut bir izin profilini belirli bir çalışana atayın.",
    users_employee_label: "Çalışan",
    users_employee_placeholder: "Çalışan seçin",
    users_profile_label: "İzin profili",
    users_profile_placeholder: "Profil seçin",
    users_reason_label: "Gerekçe (isteğe bağlı)",
    users_company_scope_label: "Şirket erişimi",
    users_company_scope_holding: "Holdingdeki tüm şirketler",
    users_company_scope_selected: "Yalnız seçtiğim şirketler",
    users_company_scope_companies_label: "Bu erişimin kapsadığı şirketler",
    users_company_scope_empty: "En az bir şirket seçin.",
    users_grant_action: "Profili ata",
    users_granting: "Atanıyor…",
    users_resolved_title: "Çözümlenen atamalar",
    users_resolved_description: "Sahipleri kiracı verilerinden listelenebilen atamalar.",
    users_all_employees: "Herkes için geçerli",
    users_no_resolved_holders: "Bu çözümlenen atama için sahip bilgisi dönmedi.",
    users_more_holders: "ve {{count}} kişi daha",
    users_unresolvable_title: "Kural tabanlı atamalar",
    users_unresolvable_description:
      "Bu atamalar, üyeliği bu ekrandan listelenemeyen hedefler üzerinden uygulanır.",
    users_target_kind: "Kapsam: {{kind}}",
    users_role: "Rol: {{role}}",
    users_revoke: "Geri al",
    users_revoking: "Geri alınıyor…",
    users_empty_title: "Aktif atama yok",
    users_empty_description: "Bir atama oluşturmak için çalışana izin profili verin.",
    users_load_more: "Daha fazla atama yükle",
    audit_title: "Denetim Günlüğü",
    audit_description: "Aktör, etkilenen varlık ve değişen alan adlarıyla kiracı faaliyetleri.",
    audit_filter_action_label: "Eylem",
    audit_loading: "Denetim olayları yükleniyor…",
    audit_error_title: "Denetim günlüğü yüklenemedi",
    audit_empty_title: "Denetim olayı yok",
    audit_empty_description: "Geçerli eylem filtresiyle eşleşen kiracı denetim olayı yok.",
    audit_actor_label: "Aktör",
    audit_actor_unresolved: "İsim çözülemedi",
    audit_actor_redacted: "Kişisel veri kaldırıldı",
    audit_entity_label: "Varlık",
    audit_changed_fields_label: "Değişen alanlar",
    audit_changed_fields_none: "Alan düzeyinde değişiklik yok",
    audit_forbidden_title: "Erişiminiz yok",
    audit_forbidden_description:
      "{{baseline}} Denetim Günlüğü ayrıca hesabınızda şu anda bulunmayan perm_audit_read yetkisini gerektirir. Erişim için bir kiracı yöneticisine başvurun.",
    audit_filter_all: "Tüm hareketler",
    audit_code_unknown: "Sistem kodu",
    audit_entity_id_title: "Kayıt kimliği",
    audit_target_deleted: "Kayıt silinmiş",
    audit_filter_entity_label: "Varlık türü",
    audit_filter_all_entities: "Tüm varlık türleri",
    audit_filter_from_label: "Başlangıç",
    audit_filter_to_label: "Bitiş",
    audit_page_size_label: "Satır",
    audit_search_label: "Ara",
    audit_search_placeholder: "Eylem, kişi, kayıt\u2026",
    audit_search_hint: "Bu sayfadaki satırları süzer. Günlüğün tamamını daraltmak için yukarıdaki filtreleri kullanın.",
    audit_table_caption: "Kiracı denetim olayları",
    audit_column_time: "Zaman",
    audit_column_action: "Eylem",
    audit_column_actor: "Aktör",
    audit_column_entity: "Varlık",
    audit_column_changed_fields: "Değişen alanlar",
    audit_page_indicator: "Sayfa {{page}} \u00b7 {{loaded}} satırın {{shown}} tanesi gösteriliyor",
    audit_prev_page: "Önceki",
    audit_next_page: "Sonraki",
    audit_no_match_title: "Bu sayfada eşleşme yok",
    audit_no_match_description:
      "Sayfadaki hiçbir satır \u201c{{term}}\u201d ile eşleşmiyor. Kutuyu temizleyin ya da yukarıdaki filtrelerle günlüğün tamamını daraltın.",
    audit_export_csv: "CSV indir",
    audit_exporting: "Hazırlanıyor\u2026",
    audit_csv_filename: "denetim-gunlugu.csv",
    audit_csv_action_code: "Eylem kodu",
    audit_export_truncated_title: "Dışa aktarma satır sınırında durdu",
    audit_export_truncated_description:
      "Dosyada eşleşen en yeni {{count}} olay var \u2014 dışa aktarma tavanı {{cap}} satır ve bu filtre daha fazlasını döndürdü. Kalanı almak için tarih aralığını daraltın.",
    audit_export_error_title: "Dışa aktarma başarısız",
    audit_action: {
      ai: {
        call: {
          completed: "Yapay zekâ çağrısı tamamlandı",
          refused_gate: "Yapay zekâ isteği güvenlik kuralıyla reddedildi",
        },
      },
      approval_chain_template: {
        created: "Onay zinciri şablonu oluşturuldu",
        deactivated: "Onay zinciri şablonu devre dışı bırakıldı",
        updated: "Onay zinciri şablonu güncellendi",
      },
      approval_request: { approve: "Onay talebi onaylandı" },
      asset: {
        created: "Demirbaş kaydı oluşturuldu",
        write_off_requested: "Varlık hurdaya ayırma talebi açıldı",
        written_off: "Varlık hurdaya ayrıldı",
      },
      assignment: {
        force_ended: "Demirbaş zimmeti zorla kapatıldı",
        initiated: "Demirbaş zimmetlendi",
        returned: "Demirbaş iade alındı",
      },
      audit: { log_read: "Denetim günlüğü görüntülendi" },
      availability_window: {
        created: "Uygunluk aralığı eklendi",
        deleted: "Uygunluk aralığı kaldırıldı",
      },
      benefit: { enrolled: "Yan hak kaydı yapıldı" },
      benefit_plan: {
        archived: "Yan hak planı arşivlendi",
        created: "Yan hak planı oluşturuldu",
        published: "Yan hak planı yayımlandı",
      },
      branding_config: { updated: "Markalama güncellendi" },
      calendar_event: {
        // "rezerve edildi", not "oluşturuldu": this action fires only on the
        // public self-scheduling path (routes/calendar.ts — an outside booker,
        // system actor). The old wording was both a mistranslation and, once
        // `created` was catalogued, the same sentence for two different codes.
        booked: "Takvim etkinliği dışarıdan rezerve edildi",
        cancelled: "Takvim etkinliği iptal edildi",
        created: "Takvim etkinliği oluşturuldu",
        rescheduled: "Takvim etkinliği yeniden planlandı",
      },
      carrier: { connected: "Yan hak sağlayıcısı bağlandı" },
      device: { registered: "Cihaz kaydedildi", updated: "Cihaz güncellendi" },
      device_credential: {
        issued: "Cihaz kimlik bilgisi verildi",
        revoked: "Cihaz kimlik bilgisi iptal edildi",
        rotated: "Cihaz kimlik bilgisi yenilendi",
      },
      employee: {
        preferred_locale: { updated: "Çalışanın dil tercihi güncellendi" },
        ui_tour_seen_version: { updated: "Ürün turu görüldü olarak işaretlendi" },
      },
      garnishment_deduction: { recorded: "İcra kesintisi işlendi" },
      garnishment_order: {
        created: "İcra kesintisi emri oluşturuldu",
        satisfied_in_full: "İcra kesintisi emri tamamen tahsil edildi",
      },
      handover_record: { signed: "Demirbaş teslim tutanağı imzalandı" },
      integrations: {
        scim: { user_provisioning_accepted: "SCIM kullanıcı aktarımı kabul edildi" },
      },
      integration: {
        installed: "Entegrasyon bağlandı",
        paused: "Entegrasyon duraklatıldı",
        reconfigured: "Entegrasyon yeniden yapılandırıldı",
        resumed: "Entegrasyon sürdürüldü",
        uninstalled: "Entegrasyon bağlantısı kaldırıldı",
      },
      kiosk: { pin_locked: "Kiosk PIN'i kilitlendi" },
      kiosk_pin_credential: {
        issued: "Kiosk PIN’i verildi",
        revoked: "Kiosk PIN'i iptal edildi",
      },
      leave_request: {
        approved: "İzin talebi onaylandı",
        cancelled: "İzin talebi iptal edildi",
        created: "İzin talebi taslak olarak oluşturuldu",
        rejected: "İzin talebi reddedildi",
        submitted: "İzin talebi onaya gönderildi",
      },
      payroll_tr: {
        master_data_updated: "Bordro ana verisi güncellendi",
        workplace_created: "İşyeri (SGK) tanımlandı",
        workplace_updated: "İşyeri (SGK) güncellendi",
      },
      pulse_cycle: {
        paused: "Nabız anketi dönemi duraklatıldı",
        started: "Nabız anketi dönemi başlatıldı",
      },
      schedule: { published: "Vardiya çizelgesi yayımlandı" },
      scim_bearer_token: {
        issued: "SCIM anahtarı verildi",
        revoked: "SCIM anahtarı iptal edildi",
      },
      notification: {
        marked_read: "Bildirim okundu işaretlendi",
        preference_updated: "Bildirim tercihi güncellendi",
        sent: "Bildirim gönderildi",
      },
      onboarding_instance: { started: "Oryantasyon süreci başlatıldı" },
      onboarding_plan: {
        created: "Oryantasyon planı oluşturuldu",
        published: "Oryantasyon planı yayımlandı",
      },
      onboarding_task: { transitioned: "Oryantasyon görevi durum değiştirdi" },
      org_scenario_apply: {
        department_change: "Departman değişikliği uygulandı",
        dissolved_department: "Departman kapatıldı",
        manager_change: "Yönetici değişikliği uygulandı",
        new_department: "Yeni departman açıldı",
        promotion: "Terfi uygulandı",
        reporting_relationship_add: "Raporlama ilişkisi eklendi",
        reporting_relationship_remove: "Raporlama ilişkisi kaldırıldı",
        team_add: "Takıma eklendi",
        team_remove: "Takımdan çıkarıldı",
        team_role_change: "Takım rolü değişti",
        termination: "İşten ayrılış uygulandı",
      },
      orphaned_punch: { purged: "Sahipsiz giriş-çıkış kaydı temizlendi" },
      notification_default: {
        cleared: "Bildirim varsayılanı kaldırıldı",
        updated: "Bildirim varsayılanı değişti",
      },
      pay_run: {
        approval_requested: "Bordro koşusu onaya gönderildi",
        approved: "Bordro koşusu onaylandı",
        calculated: "Bordro koşusu hesaplandı",
        created: "Bordro koşusu oluşturuldu",
        finalize_enqueued: "Bordro koşusu kesinleştirme sırasına alındı",
        finalized: "Bordro koşusu kesinleştirildi",
        previewed: "Bordro koşusu önizlendi",
      },
      pay_statement: { created: "Bordro pusulası oluşturuldu" },
      payslip_artifact: { issued: "Bordro belgesi düzenlendi" },
      permission_assignment: {
        granted: "İzin profili atandı",
        revoked: "İzin profili geri alındı",
      },
      permission_profile: {
        created: "İzin profili oluşturuldu",
        updated: "İzin profili güncellendi",
      },
      platform: { healthz: { checked: "Servis sağlık kontrolü yapıldı" } },
      presence_session: { closed: "İşyerinde bulunma oturumu kapatıldı" },
      pto_balance: {
        drawn: "İzin bakiyesinden düşüldü",
        restored: "İzin bakiyesi iade edildi",
      },
      recognition: { given: "Takdir verildi" },
      sar_export: { bundle_assembled: "Veri erişim talebi paketi hazırlandı" },
      session: { me: { fetched: "Oturum bilgisi okundu" } },
      severance_calculation: { computed: "Kıdem tazminatı hesaplandı" },
      shift: { swap_requested: "Vardiya değişimi talep edildi" },
      support_inquiry: { created: "Destek başvurusu oluşturuldu" },
      support_ticket: {
        created: "Destek talebi oluşturuldu",
        escalated: "Destek talebi üst kademeye taşındı",
      },
      survey: { launched: "Anket başlatıldı" },
      survey_response: { submitted: "Anket yanıtı gönderildi" },
      task: { completed: "Görev tamamlandı" },
      admin: { setup_completed: "Çalışma alanı kurulumu tamamlandı" },
      tenant_setting: { changed: "Ayar değiştirildi" },
      time_entry: {
        created: "Puantaj kaydı oluşturuldu",
        locked: "Puantaj kaydı kilitlendi",
        updated: "Puantaj kaydı güncellendi",
      },
      time_payroll_export: {
        consumed: "Bordro aktarımı bordro koşusuna alındı",
        ready: "Bordro aktarımı hazırlandı",
      },
      timesheet: {
        approved: "Puantaj onaylandı",
        assembled: "Puantaj derlendi",
        locked: "Puantaj kilitlendi",
        reopened: "Puantaj yeniden açıldı",
        submitted: "Puantaj onaya gönderildi",
      },
    },
    audit_entity: {
      ai_call: "Yapay zekâ çağrısı",
      entity_approval_chain_template: "Onay zinciri şablonu",
      entity_approval_request: "Onay talebi",
      entity_asset: "Demirbaş",
      entity_asset_assignment: "Demirbaş zimmeti",
      entity_asset_audit_cycle: "Demirbaş sayım dönemi",
      entity_asset_handover_record: "Demirbaş teslim tutanağı",
      entity_audit_event: "Denetim olayı",
      entity_availability_window: "Uygunluk aralığı",
      entity_beneficiary: "Lehtar",
      entity_benefit_carrier: "Yan hak sağlayıcısı",
      entity_benefit_plan: "Yan hak planı",
      entity_branding_config: "Markalama ayarı",
      entity_calendar_event: "Takvim etkinliği",
      entity_department: "Departman",
      entity_dependent: "Bakmakla yükümlü olunan kişi",
      entity_device: "Cihaz",
      entity_device_credential: "Cihaz kimlik bilgisi",
      entity_employee: "Çalışan",
      entity_enrollment: "Yan hak kaydı",
      entity_garnishment_deduction: "İcra kesintisi",
      entity_garnishment_order: "İcra kesintisi emri",
      entity_integration: "Entegrasyon",
      entity_kiosk_pin_credential: "Kiosk PIN kimliği",
      entity_location: "Lokasyon",
      entity_notification: "Bildirim",
      entity_notification_delivery: "Bildirim iletimi",
      entity_notification_preference: "Bildirim tercihi",
      entity_notification_tenant_default: "Bildirim varsayılanı",
      entity_ocr_job: "Belge okuma işi",
      entity_onboarding_instance: "Oryantasyon süreci",
      entity_onboarding_plan: "Oryantasyon planı",
      entity_onboarding_task: "Oryantasyon görevi",
      entity_org_scenario: "Organizasyon senaryosu",
      entity_orphaned_punch: "Sahipsiz giriş-çıkış kaydı",
      entity_pay_run: "Bordro koşusu",
      entity_pay_statement_tr: "Bordro pusulası",
      entity_payslip_artifact: "Bordro belgesi",
      entity_permission_assignment: "İzin ataması",
      entity_permission_profile: "İzin profili",
      entity_presence_session: "İşyerinde bulunma oturumu",
      entity_pto_balance: "İzin bakiyesi",
      entity_pto_request: "İzin talebi",
      entity_pulse_cycle: "Nabız anketi dönemi",
      entity_qle_event: "Yaşam olayı",
      entity_recognition: "Takdir",
      entity_report_run: "Rapor koşusu",
      entity_reporting_relationship: "Raporlama ilişkisi",
      entity_sar_request: "Veri erişim talebi",
      entity_schedule: "Vardiya çizelgesi",
      entity_scheduled_deduction: "Planlı kesinti",
      entity_scheduled_export: "Planlı dışa aktarım",
      entity_severance_calculation: "Kıdem tazminatı hesabı",
      entity_shift: "Vardiya",
      entity_signature_request: "İmza talebi",
      entity_support_inquiry: "Destek başvurusu",
      entity_support_ticket: "Destek talebi",
      entity_survey: "Anket",
      entity_survey_response: "Anket yanıtı",
      entity_task: "Görev",
      entity_team_membership: "Takım üyeliği",
      entity_tenant: "Kurum",
      entity_tenant_setting: "Kurum ayarı",
      entity_time_entry: "Puantaj kaydı",
      entity_time_payroll_export_batch: "Bordro aktarım partisi",
      entity_timesheet: "Puantaj",
      scim_request: "SCIM isteği",
      session: "Oturum",
      system: "Sistem",
    },
    target_kind: {
      everyone: "Herkes",
      role: "Bir rol",
      explicit_ids: "Seçilen çalışanlar",
      attribute_rule: "Bir nitelik kuralı",
      supergroup: "Bir süper grup",
    },
    role_name: {
      role_super_admin: "Süper yönetici",
      role_admin: "Yönetim",
      role_executive: "Üst yönetim",
      role_hr: "İK",
      role_team_lead: "Takım lideri",
      role_sar_auditor: "Veri talebi denetçisi",
      role_hr_admin: "İK yöneticisi",
      role_it_admin: "BT yöneticisi",
      role_manager: "Yönetici",
      role_finance: "Mali işler",
      role_compliance_officer: "Uyum sorumlusu",
      role_support_agent: "Destek uzmanı",
      role_employee: "Çalışan",
    },
    // C8 — SSO & Güvenlik.
    sso_title: "Girişler nasıl yapılıyor",
    sso_description: "Çalışanlarınızın kimliğini doğrulayan sunucudan canlı okunur.",
    sso_deployment_note:
      "Bunlar kurulum genelindeki ayarlardır; bu kurulumdaki tüm kurumlar için ortaktır — burada, size neyin uygulandığını bilin diye gösterilir, tek bir kurum bunları değiştiremez.",
    sso_loading: "Giriş ayarları yükleniyor…",
    sso_error_title: "Giriş ayarları yüklenemedi",
    sso_not_configured_title: "Bağlı kimlik sunucusu yok",
    sso_not_configured_body:
      "Bu kurulumda kimlik sunucusu kimlik bilgisi tanımlı değil, bu yüzden giriş ayarları okunamıyor. Aşağıdaki erişim kapsamı yine kendi kayıtlarınızdan gelir.",
    sso_unreachable_title: "Kimlik sunucusu yanıt vermedi",
    sso_unreachable_body:
      "Aşağıdaki ayarlar şu an okunamadı. Bu \"hiçbir kural uygulanmıyor\" demek değildir — önceki yapılandırma yürürlüktedir.",
    sso_realm: "Kimlik alanı",
    sso_checked_at: "{{time}} kontrol edildi",
    sso_first_factor: "Giriş bilgisi",
    sso_otp: "Tek kullanımlık kod",
    sso_password_policy: "Parola kuralları",
    sso_password_policy_on: "Uygulanıyor",
    sso_password_policy_off: "Yok",
    sso_self_registration: "Kendi kendine kayıt",
    sso_password_reset: "Kullanıcının kendi parolasını sıfırlaması",
    sso_login_with_email: "E-posta adresiyle giriş",
    sso_remember_me: "Oturumu açık tut",
    sso_brute_force: "Hatalı denemeden sonra kilitleme",
    sso_brute_force_on: "{{count}} denemeden sonra",
    sso_session_idle: "Boşta kalma süresi",
    sso_session_max: "En uzun oturum süresi",
    sso_enabled: "Açık",
    sso_disabled: "Kapalı",
    sso_no_required_credential: "Beyan edilen kimlik bilgisi yok",
    sso_providers_title: "Kimlik sağlayıcılar",
    sso_providers_description: "Çalışanlarınızın üzerinden giriş yapabileceği dış dizinler.",
    sso_providers_empty_title: "Bağlı dış sağlayıcı yok",
    sso_providers_empty_body:
      "Çalışanlarınız NovemAI'nin kendi kimlik sunucusuyla giriş yapıyor. Bir kurumsal dizini bağlamak ayrı bir kurulum adımıdır.",
    sso_provider_disabled: "Devre dışı",
    sso_coverage_title: "Kimler giriş yapabiliyor",
    sso_coverage_description: "Kaydına giriş kimliği bağlanmış çalışanlar.",
    sso_coverage_linked: "{{total}} çalışandan {{linked}} tanesi",
    sso_coverage_all_linked: "Hâlen çalışan herkes giriş yapabiliyor.",
    sso_coverage_unlinked: "{{count}} kişinin giriş kimliği yok",
    sso_coverage_unlinked_note:
      "Çalışan kayıtları var ama henüz giriş yapamıyorlar. İşten ayrılmış kişiler sayılmaz.",
    sso_sessions_title: "Kendi açık oturumlarınız",
    sso_sessions_description: "Kendi hesabınızla şu an açık olan oturumlar.",
    sso_sessions_empty: "Başka açık oturum yok.",
    sso_session_started: "{{time}} başladı",
    sso_session_last_seen: "Son hareket {{time}}",
    sso_session_ip: "IP {{ip}}",
    tab_integrations: "Entegrasyonlar",
    tab_notifications: "Bildirim Ayarları",
    tab_billing: "Faturalandırma",
    tab_companies: "Şirketler",
    companies_title: "Şirketler",
    companies_description:
      "Bu kurumun işlettiği tüzel kişilikler. Her çalışan birine bağlıdır ve bordro o şirketin vergi numarasını basar.",
    companies_loading: "Şirketler yükleniyor…",
    companies_error_title: "Şirketler yüklenemedi",
    companies_empty: "Henüz şirket yok.",
    companies_unverified: "Doğrulanmadı",
    companies_unverified_hint:
      "Bu kaydı sistem oluşturdu ve henüz kimse doğrulamadı.",
    companies_default: "Varsayılan",
    companies_archived: "Arşivli",
    companies_show_archived: "Arşivlileri göster",
    companies_name: "Şirket adı",
    companies_country: "Ülke (ISO 3166-1 alpha-2)",
    companies_currency: "Para birimi (ISO 4217)",
    companies_vkn: "Vergi kimlik numarası (VKN)",
    companies_vkn_line: "Vergi numarası: {{value}}",
    companies_value_none: "—",
    companies_mersis: "MERSİS numarası",
    companies_registration: "Ticaret sicil numarası",
    companies_add_title: "Şirket ekle",
    companies_add_submit: "Ekle",
    companies_adding: "Ekleniyor…",
    companies_edit: "Düzenle",
    companies_cancel: "Vazgeç",
    companies_save: "Kaydet",
    companies_saving: "Kaydediliyor…",
    companies_archive: "Arşivle",
    companies_archiving: "Arşivleniyor…",
    companies_vkn_locked:
      "Bu şirket için bordro fişi düzenlendi; vergi numarası artık değiştirilemez.",
    notif_title: "Bildirim kanalları",
    notif_description:
      "Her olay için varsayılanı belirleyin. Çalışanlar kendi profilinden daraltabilir; onların tercihi her zaman önce gelir.",
    notif_undeliverable:
      "Bu sürümde yalnızca uygulama içi ve e-posta teslim edilir. {{channels}} kanalları, tanımlı olsa da gönderilmez.",
    notif_email_unconfigured:
      "Bu kurulumda e-posta sunucusu tanımlı değil; açık bırakılan türlerde de e-posta gitmez.",
    notif_col_event: "Olay",
    notif_col_notify: "Bildir",
    notif_col_notify_help:
      "Olayın uygulama içi bildirim kutusuna düşmesini belirler; kapatıldığında aynı olay için e-posta da gönderilmez.",
    notif_col_email: "E-posta",
    notif_col_cadence: "E-posta sıklığı",
    notif_mandatory: "Zorunlu",
    notif_email_only:
      "Herkesin gelen kutusuna her zaman düşer; yalnızca e-postası kapatılabilir.",
    notif_no_email: "Bu olay e-posta göndermiyor.",
    notif_cannot_disable:
      "Kapatılamaz: yalnızca gelen kutusuna gider ve gelen kutusu kaydı bu ayardan bağımsız yazılır.",
    notif_overridden: "Kurum ayarı",
    notif_reset: "Varsayılana dön",
    notif_notify_aria: "Bildir: {{event}}",
    notif_email_aria: "E-posta gönder: {{event}}",
    notif_cadence_aria: "{{event}} e-postaları hangi sıklıkta gelsin",
    notification_group: {
      announcement: "Duyurular",
      asset: "Zimmet",
      approval_request: "Onaylar",
      calendar_event: "Takvim",
      employee: "Personel",
      kiosk: "Kiosk ve cihazlar",
      leave: "İzin",
      schedule: "Vardiya çizelgesi",
      shift: "Vardiyalar",
      support_ticket: "Destek",
      support_ticket_event: "Destek yanıtları",
      policy_publication: "Politikalar",
      onboarding_buddy: "Oryantasyon arkadaşı",
      review_response: "Değerlendirme formları",
      review: "360 değerlendirme",
      audit_cycle: "Varlık sayımları",
      payslip: "Bordro",
      learning: "Eğitim",
    },
    notification_channel: {
      email: "E-posta",
      in_app: "Uygulama içi",
      sms: "SMS",
      push: "Anlık bildirim",
      whatsapp: "WhatsApp",
      slack: "Slack",
      teams: "Microsoft Teams",
      webhook: "Web kancası",
    },
    notification_type: {
      audit_cycle: {"started": "Varlık sayımı başladı"},
      payslip: { available: "Bordro hazır" },
      onboarding_buddy: {"proposed": "Oryantasyon arkadaşı daveti", "accepted": "Arkadaşlık kabul edildi", "declined": "Arkadaşlık kabul edilmedi", "attention_required": "Oryantasyon arkadaşı programında işlem gerekiyor"},
      policy_publication: { available: "Okunacak yeni politika", reminder: "Politika okuma hatırlatması" },
      review_response: { assigned: "Size değerlendirme formu atandı" },
      review: { shared: "360 değerlendirme sonuçlarınız paylaşıldı" },
      learning: { reminder: "Eğitim hatırlatması" },

      announcement: { published: "Yeni şirket duyurusu", reminder: "Duyuru okundu onayı hatırlatması" },
      asset: {
        assigned: "Size zimmet atandı",
        vehicle: {
          trafik_sigortasi_expiring: "Trafik sigortası bitmek üzere",
          muayene_expiring: "Araç muayenesi bitmek üzere",
          muayene_expired: "Araç muayenesi doldu",
        },
      },
      approval_request: {
        approved: "Talebiniz onaylandı",
        cancelled: "Talebiniz iptal edildi",
        created: "Onayınız bekleniyor",
        expired: "Talebinizin süresi doldu",
        rejected: "Talebiniz reddedildi",
        step_advanced: "Onay sizin adımınıza geldi",
      },
      calendar_event: {
        cancelled: "Toplantı iptal edildi",
        created: "Yeni toplantı daveti",
        reminder_due: "Toplantı birazdan başlıyor",
        rescheduled: "Toplantı ertelendi",
      },
      employee: {
        hired: "Yeni bir arkadaş işe başladı",
      },
      kiosk: {
        pin_locked: "Kiosk PIN'i kilitlendi",
      },
      leave: {
        approved: "İzin onaylandı",
        cancelled: "İzin iptal edildi",
        rejected: "İzin reddedildi",
      },
      schedule: {
        published: "Vardiya çizelgesi yayımlandı",
      },
      shift: {
        swapped: "Vardiya değişti",
      },
      support_ticket: {
        assigned: "Destek talebi size atandı",
        created: "Destek talebi açıldı",
        resolved: "Destek talebi çözüldü",
        sla_breached: "Destek talebi SLA'yı aştı",
      },
      support_ticket_event: {
        appended: "Destek talebine yanıt geldi",
      },
    },
    connectors_loading: "Entegrasyonlar yükleniyor…",
    connectors_error_title: "Entegrasyonlar yüklenemedi",
    connectors_thin_note:
      "Gönderilen bildirim yalnızca ne olduğunu ve ne zaman olduğunu taşır — çalışan verisi taşımaz. Ucunuza olay türü ve bir kayıt numarası gider; isim, ücret veya kimlik bilgisi gitmez.",
    connectors_insecure_note:
      "Bu kurulumda düz http:// ve iç ağ adreslerine izin veriliyor. Bu bir geliştirme ayarıdır ve üretimde açık olmamalıdır.",
    connector_webhook_title: "Webhook — giden olay akışı",
    connector_webhook_blurb:
      "İzin, bordro ve personel olaylarını gerçekleştiği anda kendi sisteminize gönderin.",
    connector_slack_title: "Slack",
    connector_slack_blurb: "Takdir ve duyurular çalışma alanınızda.",
    connector_state_connected: "Bağlı",
    connector_state_paused: "Duraklatıldı",
    connector_state_errored: "Hata veriyor",
    connector_state_available: "Bağlanabilir",
    connector_state_unconfigured: "Kurulmamış",
    connector_unconfigured_body:
      "Bu konnektör, Novem HR kurulumunuza henüz verilmemiş değerlere ihtiyaç duyuyor. Kurulumu yöneten kişiden şunları tanımlamasını isteyin:",
    connector_destination: "Hedef adres",
    connector_destination_placeholder: "https://hooks.ornek.com/novemai",
    connector_connect: "Bağlan",
    connector_save_destination: "Adresi kaydet",
    connector_test: "Bağlantıyı test et",
    connector_sync_now: "Şimdi gönder",
    connector_pause: "Duraklat",
    connector_resume: "Sürdür",
    connector_remove: "Bağlantıyı kaldır",
    connector_last_sync: "Son gönderim",
    connector_never_synced: "Henüz gönderim yapılmadı",
    connector_pending: "Gönderilmeyi bekleyen",
    connector_last_error: "Son hata: {{code}}",
    connector_probe_ok: "Ucunuz {{ms}} ms içinde {{status}} yanıtı verdi.",
    connector_probe_failed: "Ucunuz çağrıyı kabul etmedi ({{reason}}).",
    connector_sync_ok: "{{count}} olay gönderildi.",
    connector_sync_nothing: "Gönderilecek yeni olay yok.",
    connector_sync_failed: "Gönderim başarısız ({{reason}}).",
    connector_run_ok: "{{count}} gönderildi",
    connector_run_failed: "Başarısız",
    connector_secret_title: "Bu imzalama anahtarını şimdi kaydedin",
    connector_secret_body:
      "Ucunuz, gelen bildirimin gerçekten bizden geldiğini doğrulamak için buna ihtiyaç duyar. Yalnızca bir kez gösterilir ve bir daha alınamaz — kaybolursa bağlantıyı kaldırıp yeniden kurarak yenisini alırsınız.",
    connector_secret_dismiss: "Kaydettim",
    scim_tokens_title: "SCIM sağlama jetonları",
    scim_tokens_blurb:
      "Kimlik sağlayıcınızın çalışan kayıtlarını otomatik oluşturmasını ve güncellemesini sağlar.",
    scim_tokens_loading: "Jetonlar yükleniyor…",
    scim_tokens_empty: "Henüz jeton üretilmedi.",
    scim_token_label: "Jeton adı",
    scim_token_label_placeholder: "Okta — canlı",
    scim_token_issue: "Jeton üret",
    scim_token_revoke: "İptal et",
    scim_token_active: "Etkin",
    scim_token_revoked: "İptal edildi",
    scim_token_stale: "Kullanılamaz — yenisini üretin",
    scim_token_unverified: "Doğrulanamıyor",
    scim_token_copy: "Kopyala",
    scim_token_copied: "Kopyalandı",
    scim_token_new_title: "Bu jetonu şimdi kopyalayın",
    scim_token_new_body:
      "Kimlik sağlayıcınızın SCIM ayarlarına yapıştırın. Yalnızca bir kez gösterilir ve bir daha alınamaz — kaybolursa iptal edip yenisini üretin.",
    scim_token_dismiss: "Kaydettim",
    scim_token_allowlist_note:
      "Bu kurulumda ayrıca bir özet listesi tutuluyor. Bu değeri sistem yöneticinize iletin, aksi hâlde jeton reddedilir.",
    sso_credential: {
      password: "Parola",
      otp: "Tek kullanımlık kod",
      "webauthn-passwordless": "Geçiş anahtarı",
      webauthn: "Güvenlik anahtarı",
      "kerberos": "Kerberos",
    },
    region: {
      region_tr: "Türkiye",
      region_eu: "Avrupa Birliği",
      region_us: "Amerika Birleşik Devletleri",
      region_apac_reserved: "Asya-Pasifik (ayrılmış)",
      region_ca_reserved: "Kanada (ayrılmış)",
      on_prem: "Kendi sunucularınız",
    },
    unresolvable_reason: {
      target_kind_not_enumerable:
        "Bu hedefin üyeliği erişim kontrolü sırasında değerlendirilir; bu yüzden üyeler bu ekranda listelenemez.",
    },
    // Sekme başlık satırları (SettingsTabHeader).
    users_description: "Hangi yetki profilinin kimde olduğu ve nerede geçerli olduğu.",
    sso_tab_description: "Giriş yöntemi, kimlik sağlayıcılar ve açık oturumlarınız.",
    integrations_description: "Bağlayıcılar, SCIM hesap yönetimi ve webhook uç noktaları.",
    audit_data_export_link: "Veri dışa aktarımı (KVKK)",
    // Şirket profili — varsayılan tüzel kişilikten okunan kimlik kartı.
    company_hero_loading: "Şirket kimlik bilgileri yükleniyor…",
    company_hero_error: "Şirket kimlik bilgileri yüklenemedi.",
    company_hero_source: "Varsayılan şirket kaydından okunur.",
    company_hero_no_default: "Hesabınız için varsayılan şirket bulunamadı.",
    company_hero_edit: "Düzenle",
    company_hero_vkn: "Vergi kimlik no (VKN)",
    company_hero_mersis: "MERSİS no",
    company_hero_address: "Adres",
    company_hero_not_set: "Girilmemiş",
    // Entegrasyonlar — tümünü senkronize et.
    connectors_sync_all: "Tümünü senkronize et",
    connectors_sync_all_results: "Senkronizasyon sonuçları",
    // Webhook uç noktaları (/v1/webhooks).
    webhooks_title: "Webhook uç noktaları",
    webhooks_blurb: "İmzalı olay gönderimlerini kendi HTTPS adresinizde alın.",
    webhooks_loading: "Webhook uç noktaları yükleniyor…",
    webhooks_empty: "Henüz webhook uç noktası yok.",
    webhooks_no_delete_note:
      "Uç noktalar silinemez. Gönderimleri durdurmak için uç noktayı duraklatın.",
    webhook_url_label: "Uç nokta adresi",
    webhook_url_placeholder: "https://ornek.com/webhooks/novemai",
    webhook_events_label: "Olaylar",
    webhook_events_hint:
      "Olay adlarını virgülle ayırın. Sunucu tanımadığı adları reddeder.",
    webhook_register: "Uç nokta ekle",
    webhook_active: "Etkin",
    webhook_paused: "Duraklatıldı",
    webhook_created_at: "Eklenme: {{time}}",
    webhook_pause: "Duraklat",
    webhook_resume: "Sürdür",
    webhook_rotate: "Anahtarı yenile",
    webhook_rotate_confirm_body:
      "Geçerli imzalama anahtarı hemen çalışmaz olur. Alıcınız gönderimleri yeni anahtarla doğrulamalıdır.",
    webhook_rotate_confirm: "Şimdi yenile",
    webhook_cancel: "Vazgeç",
    webhook_secret_title: "Bu imzalama anahtarını şimdi kaydedin",
    webhook_secret_body:
      "Anahtar yalnız bu kez gösterilir. Kaybederseniz anahtarı yenileyerek yenisini alın.",
    webhook_secret_dismiss: "Kaydettim",
    webhook_deliveries_title: "Son gönderimler",
    webhook_deliveries_show: "Gönderimleri göster",
    webhook_deliveries_hide: "Gönderimleri gizle",
    webhook_deliveries_loading: "Gönderimler yükleniyor…",
    webhook_deliveries_empty: "Henüz gönderim yok.",
    webhook_delivery_attempt: "{{n}}. deneme",
    webhook_delivery_http: "HTTP {{status}}",
    webhook_delivery_retry: "Yeniden dene",
    webhook_delivery_retry_queued: "Yeniden deneme sıraya alındı. Sonraki gönderim turu onu gönderir.",
    webhook_delivery_status: {
      pending: "Bekliyor",
      delivered: "Teslim edildi",
      failed: "Başarısız",
      dead_letter: "Durduruldu",
    },
  },
} as const;
